// ts-check
import React, { useMemo, useCallback } from 'react';
import { Select, SelectOption, SelectList, MenuToggle, Badge } from '@patternfly/react-core';

import SimpleTable, { SortDirection } from 'components/Common/SimpleTable/SimpleTable';
import { useTranslation } from 'localization/useTranslation';
import _ from 'lodash';
import { UserPermissions } from 'utils/types';
import SelectUsersModal from 'components/Modals/SelectUsersModal';
import useBoolean from 'utils/hooks/useBoolean';
import { VisibleColumns } from 'components/RelationTable/RelationTable';
import T from 'localization/LocalizationKeys';

/**
 * @type {(import('components/Common/SimpleTable/SimpleTable').ColumnType<ResourceType> & { columnKey: string })[]}
 */
const defaultTableColumns = [
  {
    columnKey: VisibleColumns.displayName,
    label: T.common.displayname,
    path: 'displayName',
    searchItem: true,
    isSortable: true
  },
  {
    columnKey: VisibleColumns.id,
    label: T.common.id,
    path: 'id',
    isSortable: true
  },
  {
    columnKey: VisibleColumns.type,
    label: T.common.type,
    path: 'type',
    isSortable: true
  }
];
const createRelation = (orgId, id, subjectType, relation) => {
  return {
    resourceType: 'organization',
    resourceId: orgId,
    relation: relation,
    subjectType: subjectType,
    subjectId: id
  };
};

const DefaultSortBy = {
  path: 'displayName',
  direction: SortDirection.DESC
};

const MemberSelect = ({ key, options, value, onAddRelation, onDeleteRelation }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedItems, setSelectedItems] = React.useState(value);
  const onToggleClick = () => {
    setIsOpen(!isOpen);
  };

  const onSelect = (unusedEvent, selection) => {
    if (_.includes(selectedItems, selection)) {
      setSelectedItems(_.without(selectedItems, selection));
      onDeleteRelation(selection);
    } else {
      setSelectedItems(_.concat(selectedItems, selection));
      onAddRelation(selection);
    }
  };
  const toggle = toggleRef => (<MenuToggle ref={toggleRef} onClick={onToggleClick} isExpanded={isOpen} style={{ width: '200px' }}>
    Member Status
    { _.size(selectedItems) > 0 && <Badge isRead>{ _.size(selectedItems) }</Badge> }
  </MenuToggle>);
  return (
    <Select
      role="menu"
      id="checkbox-select-menu"
      isOpen={isOpen}
      selected={selectedItems}
      onSelect={onSelect}
      onOpenChange={nextOpen => setIsOpen(nextOpen)}
      toggle={toggle}
      popperProps={{ appendTo: document.body }}
    >
      <SelectList>
        { _.map(options, (option) => (
          <SelectOption key={`${key}-${option.id}`}hasCheckbox value={option.id} isSelected={selectedItems.includes(option.id)}>
            { option.label }
          </SelectOption>
        )) }
      </SelectList>
    </Select>
  );
};

const MemberRelationTable = ({ orgId, resources, isLoading, updateRelationsFn, deleteRelationsFn }) => {
  const T = useTranslation();
  const [isShowingSelection, , closeSelection, toggleSelection] = useBoolean(false);

  const options = useMemo(() => {
    return [
      { id: UserPermissions.Owner, label: T.localize(T.permissions.owner) },
      { id: UserPermissions.Manager, label: T.localize(T.permissions.manager) },
      { id: UserPermissions.Member, label: T.localize(T.permissions.member) },
      { id: UserPermissions.ManageAssets, label: T.localize(T.permissions.manageAssets) },
      { id: UserPermissions.ManageDashboards, label: T.localize(T.permissions.manageDashboards) },
      { id: UserPermissions.ManageUsers, label: T.localize(T.permissions.manageUsers) },
      { id: UserPermissions.ManageTrees, label: T.localize(T.permissions.manageTrees) },
      { id: UserPermissions.ManageModels, label: T.localize(T.permissions.manageModels) },
      { id: UserPermissions.ManageAlarms, label: T.localize(T.permissions.manageAlarms) },
      { id: UserPermissions.ManageOrganizations, label: T.localize(T.permissions.manageOrganizations) },
      { id: UserPermissions.ManageProviders, label: T.localize(T.permissions.manageProviders) },
      { id: UserPermissions.ManageProviderClients, label: T.localize(T.permissions.manageProviderClients) }
    ];
  }, [T]);


  const columns = useMemo(() => {
    return [
      {
        label: T.common.displayname,
        path: 'displayName',
        searchItem: true,
        isSortable: true
      },
      {
        label: T.common.login,
        path: 'email',
        isSortable: true,
        searchItem: true
      },
      {
        label: T.common.type,
        path: 'type',
        isSortable: true
      },
      {
        label: T.common.relation,
        path: '.',
        isSortable: false,
        render: (row) => {
          function onAddRelation(relation) {
            const newRelationObj = createRelation(orgId, row.id, row.type, relation);
            updateRelationsFn([newRelationObj]);
          }
          function onDeleteRelation(relation) {
            const oldRelationObj = createRelation(orgId, row.id, row.type, relation);
            deleteRelationsFn(oldRelationObj);
          }
          return (
            <>
              <MemberSelect key={row.id} options={options} value={row.relations} onAddRelation={onAddRelation} onDeleteRelation={onDeleteRelation}/>
            </>
          );
        }
      }
    ];
  }, [T.common.displayname, T.common.login, T.common.relation, T.common.type, deleteRelationsFn, options, orgId, updateRelationsFn]);


  const [available, selected] = _.partition(resources, resource => _.isEmpty(resource.relations));

  const addMemberRelations = useCallback((selected) => {
    return [
      {
        label: T.permissions.member,
        onClick: () => {
          const relations = selected.map((row) => {
            return createRelation(orgId, row.id, row.type, UserPermissions.Member);
          });
          updateRelationsFn(relations);
        }
      },
      {
        label: T.permissions.manager,
        onClick: () => {
          const relations = selected.map((row) => {
            return createRelation(orgId, row.id, row.type, UserPermissions.Manager);
          });
          updateRelationsFn(relations);
        }
      },
      {
        label: T.permissions.owner,
        onClick: () => {
          const relations = selected.map((row) => {
            return createRelation(orgId, row.id, row.type, UserPermissions.Owner);
          });
          updateRelationsFn(relations);
        }
      }
    ];
  }, [orgId, updateRelationsFn, T.permissions.member, T.permissions.manager, T.permissions.owner]);


  const globalActions = useMemo(/** @returns {import('components/Common/SimpleTable/SimpleTable').GlobalAction[]} */() => {
    return [
      {
        label: T.common.add,
        onClick: () => {
          toggleSelection();
        }
      }
    ];
  }, [T.common.add, toggleSelection]);
  return (
    <>
      <SimpleTable
        globalActions={globalActions}
        compact
        columns={columns}
        data={selected}
        isLoading={isLoading}
        sortBy={DefaultSortBy}
      />
      <SelectUsersModal
        title={T.common.add}
        tableColumns={defaultTableColumns}
        isOpen={isShowingSelection}
        onClose={closeSelection}
        available={available}
        groupId={orgId}
        updateRelationsFn={updateRelationsFn}
        globalActionsArray={addMemberRelations}
      />
    </>
  );
};

export default MemberRelationTable;
